import React, { useEffect, useState } from 'react';
import { Heart, Send, X } from 'lucide-react';
import { IoMusicalNotes } from 'react-icons/io5';
import { fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';
import { useNavigate } from 'react-router-dom';

const EngagementModal = ({
  recipientName,
  UserName,
  Thumbnail,
  Description,
  picture,
  showSendSameWishButton,
  wish = 'Best Wishes',
  venueId,
  userCoins = 0,
  onSuccess,
  onOpenShoutoutModal,
  onClose,
  venue_status,
}) => {
  const navigate = useNavigate();
  const [showInsufficient, setShowInsufficient] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pricePacks, setPricePacks] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profile, setProfile] = useState({});
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Fetch user profile to get current coins
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetchWithAuth(API_ENDPOINTS.USERS, {
          method: 'GET',
        });

        if (!response.ok) return;
        const data = await response.json();
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, []);

  // --- helpers for highlighting description ---
  const escapeRegExp = str => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightText = (text, username, recName) => {
    if (!text) return null;

    const names = [username, recName].filter(Boolean);
    if (names.length === 0) return text;

    const pattern = names.map(n => `\\b${escapeRegExp(n)}\\b`).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    return text.split(regex).map((part, i) => {
      const isName = names.some(
        n => part.toLowerCase() === String(n).toLowerCase()
      );
      return isName ? (
        <span key={i} style={{ fontWeight: 700, color: '#C81620' }}>
          {part}
        </span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      );
    });
  };

  const renderDescription = () => {
    const descriptionStr = (Description || '').trim();

    // Fallback to old sentence if Description is empty
    if (!descriptionStr) {
      return (
        <p
          style={{
            color: '#6b7280',
            marginBottom: '32px',
            fontSize: '16px',
            lineHeight: 1.6,
          }}
        >
          <span style={{ fontWeight: 700, color: '#C81620' }}>
            {(UserName || 'Someone') + ' '}
          </span>
          just gave a shoutout to{' '}
          <span style={{ fontWeight: 700, color: '#C81620' }}>
            {recipientName}
          </span>
          .<br />
          Want to add yours?
        </p>
      );
    }

    // Use Description: highlight names + break after first period
    const hasDot = descriptionStr.includes('.');
    const [firstPart, ...restParts] = descriptionStr.split('.');
    const rest = restParts.join('.').trim();

    return (
      <p
        style={{
          color: '#6b7280',
          marginBottom: '32px',
          fontSize: '16px',
          lineHeight: 1.6,
        }}
      >
        {highlightText(firstPart, UserName, recipientName)}
        {hasDot ? '.' : ''}
        {rest ? (
          <>
            <br />
            {highlightText(rest, UserName, recipientName)}
          </>
        ) : null}
      </p>
    );
  };

  // Preload price packs for coin purchase
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const vId = venueId || localStorage.getItem('vId');
        if (!vId) return;
        const response = await fetchWithAuth(
          API_ENDPOINTS.ORDER_PRICE_BY_VENUE(vId)
        );
        if (!response.ok) return;
        const data = await response.json();
        const visiblePacks = (data || []).filter(pack => !pack.isHidden);
        setPricePacks(visiblePacks);
      } catch (_) {
        // ignore
      }
    };
    fetchPrice();
  }, [venueId]);

  const handleWishAgain = () => {
    if (venue_status !== 'active') {
      setIsError(true);
      setErrorMsg("DJ's taking a break, request will open soon");
      setTimeout(() => {
        setIsError(false);
        setErrorMsg('');
      }, 3000);
      return;
    }
    if (profile.coins < shoutOutCost) {
      navigate('/insufficient-coins', {
        state: {
          shoutOutDetails: {
            occasion: `${wish}`,
            name: `${recipientName}`,
            description: '',
            isMidnightShoutOut: false,
            picture: picture,
          },
          cost: shoutOutCost,
          priceData: pricePacks,
          walletCoins: userCoins,
        },
      });
      return;
    }
    setShowConfirmation(true);
  };

  const submitWishAgain = async () => {
    if (profile.coins < shoutOutCost) {
      setShowConfirmation(false);
      return;
    }
    if (!venueId && !localStorage.getItem('vId')) {
      setShowConfirmation(false);
      onOpenShoutoutModal?.();
      onClose?.();
      return;
    }
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('tringAuthToken');
      if (!token) {
        setShowConfirmation(false);
        return;
      }
      const vId = venueId || localStorage.getItem('vId');
      const res = await fetchWithAuth(API_ENDPOINTS.SHOUTOUT_REQUEST(vId), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shoutOut: `${wish} ${recipientName}`,
          wish: `${wish}`,
          recipientName: `${recipientName}`,
          additionalInfo: '',
          isMidnightShoutOut: false,
          picture: picture,
        }),
      });
      if (!res.ok) {
        setShowConfirmation(false);
        return;
      }
      if (onSuccess) onSuccess();
      onClose?.();
    } catch (_) {
      setShowConfirmation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWishSomeoneElse = () => {
    onOpenShoutoutModal?.();
    onClose?.();
  };

  const shoutOutCost = parseInt(
    localStorage.getItem('shoutOutCost') || '100',
    10
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 50,
        animation: 'fadeIn 0.3s ease-out',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '32px',
          maxWidth: '384px',
          width: '100%',
          margin: '0 16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          overflow: 'hidden',
          animation: 'slideInFromBottom 0.5s ease-out',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            backgroundColor: '#f3f4f6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s',
            zIndex: 20,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#e5e7eb';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          aria-label="Close"
        >
          <X style={{ width: '16px', height: '16px', color: '#6b7280' }} />
        </button>

        {/* Background Gradient */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'linear-gradient(to bottom right, #fef2f2, #fdf2f8, #faf5ff)',
            opacity: 0.3,
          }}
        />

        {/* Content */}
        <div style={{ textAlign: 'center', position: 'relative', zIndex: 10 }}>
          <div
            style={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              animation: 'pulse 2s infinite',
              background: Thumbnail
                ? 'transparent'
                : 'linear-gradient(135deg, #ec4899, #8b5cf6)',
            }}
          >
            <span style={{ fontSize: '32px', animation: 'bounce 1s infinite' }}>
              {picture ? (
                <img
                  src={picture}
                  alt="picture"
                  style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              ) : Thumbnail ? (
                <img
                  src={Thumbnail}
                  alt="thumbnail"
                  style={{
                    width: '80px',
                    height: '80px',
                  }}
                />
              ) : (
                <Heart
                  style={{ width: '70px', height: '70px', color: 'white' }}
                />
              )}
            </span>
          </div>

          <h2
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '12px',
              letterSpacing: '-0.025em',
            }}
          >
            Spread More Wishes!
          </h2>

          {renderDescription()}

          {/* Buttons */}
          {showSendSameWishButton ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                gap: '16px',
              }}
            >
              <button
                onClick={isSubmitting ? undefined : handleWishAgain}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  background: 'linear-gradient(to right, #C2185B, #7B1FA2)',
                  color: 'white',
                  padding: '16px 24px',
                  borderRadius: '16px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: isSubmitting ? 0.8 : 1,
                }}
              >
                <img
                  src="/images/coin.svg"
                  alt="coin"
                  style={{ width: 20, height: 20 }}
                />
                <span>Wish {recipientName} Again</span>
              </button>

              <button
                onClick={handleWishSomeoneElse}
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  background:
                    'linear-gradient(90deg, #C81620 -13.89%, #8A325D 93.25%)',
                  color: 'white',
                  padding: '14px 20px',
                  borderRadius: '16px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                <Send
                  style={{ width: '20px', height: '20px', color: 'white' }}
                />
                <span>Wish Someone Else</span>
              </button>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
              }}
            >
              <button
                onClick={handleWishSomeoneElse}
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  background:
                    'linear-gradient(90deg, #C81620 -13.89%, #8A325D 93.25%)',
                  color: 'white',
                  padding: '14px 20px',
                  borderRadius: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              >
                <img
                  src="/images/coin.svg"
                  alt="coin"
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
                <span>Send Yours Now</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal - When user has enough coins */}
      {showConfirmation && profile.coins >= shoutOutCost && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100dvh',
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
            zIndex: 3000,
          }}
          onClick={() => setShowConfirmation(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '22px',
              maxWidth: 370,
              width: '90%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
              overflow: 'hidden',
              position: 'relative',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                background: 'linear-gradient(90deg,#c91721 0%,#8a325d 100%)',
                padding: '18px 0 12px 0',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: 20,
                  letterSpacing: '.02em',
                }}
              >
                Send a Shout-Out
              </div>
            </div>

            <div
              style={{
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#fff',
                borderBottom: '1px solid #F1F1F1',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <div style={{ fontSize: 18, fontWeight: 500, color: '#222' }}>
                  Make them feel special💖
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <img
                  src="/images/coin.svg"
                  alt="coin"
                  style={{
                    width: 30,
                    height: 30,
                    marginBottom: 4,
                  }}
                />
                <div
                  style={{
                    color: '#9F2A4A',
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  {shoutOutCost} coins
                </div>
              </div>
            </div>

            <div
              style={{
                borderTop: '0.5px solid black',
                display: 'flex',
              }}
            >
              <button
                style={{
                  flex: 1,
                  border: 'none',
                  background: '#fff',
                  borderRadius: '0 0 0 22px',
                  fontSize: 17,
                  color: '#C91721',
                  padding: '15px 0',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 1,
                  borderRadius: '0 0 22px 0',
                  border: '0.5px solid #CC151E',
                  fontSize: 17,
                  fontWeight: 500,
                  color: '#fff',
                  cursor: 'pointer',
                  background:
                    'linear-gradient(90deg, #CC151E -18.35%, #873662 108.23%)',
                }}
                onClick={submitWishAgain}
                disabled={isSubmitting}
              >
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}
      {isError && (
        <div
          style={{
            position: 'fixed',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            color: 'white',
            background: 'rgba(236, 236, 236, 1)',
            borderRadius: '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.32)',
            padding: '16px 24px',
            minWidth: '300px',
            maxWidth: '90vw',
            animation: 'slideDown 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <img
            src="/images/logo.svg"
            style={{ width: '18px', height: '18px', flexShrink: 0 }}
          />
          <div
            style={{
              color: 'black',
              fontSize: '13px',
              lineHeight: '1.4',
              textAlign: 'start',
              whiteSpace: 'nowrap', // ✅ single line
              overflow: 'hidden', // ✅ cut off overflow
              textOverflow: 'ellipsis', // ✅ show "..." if too long
            }}
          >
            {typeof errorMsg === 'string'
              ? errorMsg
              : errorMsg?.message || 'Something went wrong'}
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementModal;
